import { Injectable, inject } from '@angular/core'
import { Observable, delay, of } from 'rxjs'
import { MockService } from './mock.service'
import { ApiRequestService } from './api-request.service'
import { FetchService } from './fetch.service'
import { LogService } from './log.service'
import { IAsset, IAssetCategoriesResponse, IAssetCategory, IAssetsResponse, IGetAssetsOptions, IMake, IMakeResponse } from '../interfaces/asset.interface'
import { HttpErrorResponse } from '@angular/common/http'
import { FilterService } from './filter.service'
import { ClientService } from './client.service'
import { IPagination } from '../interfaces/api-request.interface'
import { GlobalDataService } from './global-data.service'
import { EnvironmentService } from './environment.service'

@Injectable({
  providedIn: 'root',
})
export class AssetService {
  private mockService = inject(MockService)
  private fetchService = inject(FetchService)
  private apiRequestService = inject(ApiRequestService)
  private logger = inject(LogService)
  private filterService = inject(FilterService)
  private clientService = inject(ClientService)
  private globalDataService = inject(GlobalDataService)
  private environmentService = inject(EnvironmentService)


  assetKeysForApi: (keyof IAsset)[] = [
    'display_meter_uom',
    'equipment_number',
    'serial_number',
    'detailed_description',
    'listing_date',
    'list_price',
    'list_price_native',
    'list_price_rfx_date',
    'list_price_native_currency',
    'currency_type',
    'compare_at_price_multiplier',
    'lowest_monthly_payment',
    'lowest_monthly_payment_rfx_date',
    'category_id',
    'subcategory_id',
    'filter_category',
    'filter_sub_category',
    'branch_city',
    'branch_state',
    'branch_contact',
    'branch_email',
    'branch_phone',
    'year',
    'make',
    'model',
    'meter_hours',
    'meter_miles_precise',
    'is_featured',
    'is_warranty_eligible',
    'has_approved_photos',
    'is_popular',
    'is_low_hours',
    'is_financing_eligible',
    'is_just_listed',
    'image',
    'updated_date'
  ]

  getMakes(): Observable<IMakeResponse> {
    this.logger.debug('AssetService.getMakes()')

    if (this.environmentService.get().useMock) {
      return of(this.mockService.getMakes()).pipe(delay(500)) as Observable<IMakeResponse>
    }

    return this.fetchService.getRequest<IMakeResponse>(this.apiRequestService.buildEndpoint('webshop/makes')) as Observable<IMakeResponse>
  }

  getCategories(pagination: IPagination, fields: Array<keyof IAssetCategory>): Observable<IAssetCategoriesResponse> {
    const queryParams = {
      lang: this.globalDataService.selectedLanguage().code,
    }

    this.logger.debug('AssetService.getCategories() pagination: ', pagination, ' fields: ', fields, ' lang: ', this.globalDataService.selectedLanguage().code)
    if (this.environmentService.get().useMock) {
      return of(this.mockService.getAssetCategories(pagination)).pipe(delay(500))
    }

    return this.fetchService.postRequest<IAssetCategoriesResponse>(`${this.apiRequestService.buildEndpoint(`category`, queryParams)}`, {
      filters: {},
      pagination,
      fields,
    }) as Observable<IAssetCategoriesResponse>
  }

  getAsset(equipmentNumber: string): Observable<IAsset | HttpErrorResponse> {
    const queryParams = {
      lang: this.globalDataService.selectedLanguage().code,
      meter_uod: this.clientService.getMeterUod(),
    }

    this.logger.debug('AssetService.getAsset() equipmentNumber: ', equipmentNumber)
    if (this.environmentService.get().useMock) {
      return of(this.mockService.getAssets().data.find(asset => asset.equipment_number.toLowerCase() === equipmentNumber.toLowerCase()) as IAsset).pipe(delay(500))
    }
    const endpoint = this.apiRequestService.buildEndpoint(`equipment/${encodeURIComponent(equipmentNumber)}`, queryParams)
    return this.fetchService.getRequest<IAsset>(endpoint)
  }

  getAssets(options: IGetAssetsOptions): Observable<IAssetsResponse | HttpErrorResponse> {
    const {
      pagination = {
        direction: 'asc',
        field: 'equipment_number',
        index: 0,
        size: 10,
      },
      fields = this.assetKeysForApi,
      filters = {},
      queryParams = {}
    } = options
    pagination.direction = pagination.direction ?? 'asc'
    pagination.field = pagination.field ?? 'equipment_number'
    this.logger.debug('AssetService.getAssets() pagination: ', pagination)
    if (this.environmentService.get().useMock) {
      return of(this.mockService.getAssets(pagination)).pipe(delay(500))
    }
    queryParams.meter_uod = this.clientService.getMeterUod()
    queryParams.lang = this.globalDataService.selectedLanguage().code

    return this.fetchService.postRequest<IAssetsResponse>(`${this.apiRequestService.buildEndpoint('equipment', queryParams)}`, {
      filters,
      pagination,
      fields,
    }) as Observable<IAssetsResponse>
  }

  getFeaturedAssets(total: number): Observable<IAssetsResponse | HttpErrorResponse> {
    this.logger.debug('AssetService.getFeaturedAssets() total: ', total)
    return this.getAssets({
      pagination: {
        direction: 'asc',
        field: 'equipment_number',
        index: 0,
        size: total,
      },
      filters: this.filterService.build(({ and, is }) => and(
        is.listed,
        is.featured,
      )),
    })
  }

  getSimilarAssets(equipmentNumber: string, total: number): Observable<IAssetsResponse | HttpErrorResponse> {
    this.logger.debug('AssetService.getSimilarAssets() total: ', total, ' equipmentNumber: ', equipmentNumber)
    if (this.environmentService.get().useMock) {
      return of(this.mockService.getAssets({
        direction: 'asc',
        field: 'equipment_number',
        index: 0,
        size: total,
      })).pipe(delay(500))
    }
    const endpoint = this.apiRequestService.buildEndpoint(`equipment/similar/${encodeURIComponent(equipmentNumber)}`, {
      count: `${total}`,
      meter_uod: this.clientService.getMeterUod(),
      lang: this.globalDataService.selectedLanguage().code,
    })
    return this.fetchService.getRequest<IAssetsResponse>(endpoint)
  }

  getAssetsById(total: number, assetIds: string[]): Observable<IAssetsResponse | HttpErrorResponse> {
    this.logger.debug('AssetService.getAssetsById() total: ', total, ' assetIds: ', assetIds)
    return this.getAssets({
      pagination: {
        direction: 'asc',
        field: 'equipment_number',
        index: 0,
        size: 10,
      },
      filters: this.filterService.build(({ and, is, or, inArray, variable }) => and(
        is.listed,
        or(
          inArray(
            variable('equipment_number'),
            [...assetIds]
          ),
          inArray(
            variable('display_equipment_number'),
            [...assetIds]
          ),
        )
      )),
    })
  }
}
